import React, { useCallback, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { css } from 'styled-components';

import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { useWebp } from '@r1-frontend/shared/hooks/useWebp';

import { IShopItem } from '~/src/api/content/shop/getShopItems';
import { breakPoints } from '~/src/constants/breakpoints';
import { getPriceOptions } from '~/src/entities/equipment';
import { convertShopPrices } from '~/src/entities/price';
import { IBundle } from '~/src/entities/tariff';
import Card from '~/src/features/movix-new/Card';
import ConnectionInfo from '~/src/features/movix-new/ConnectionInfo';
import { ELABELS } from '~/src/features/movix-new/constants';
import { CARDS, TextInfo } from '~/src/features/movix-new/data';
import FilmsBlock from '~/src/features/movix-new/FilmsBlock';
import Header from '~/src/features/movix-new/Header';
import MovixBenefits from '~/src/features/movix-new/MovixBenefits';
import Popup, { EEquipmentType, IPopupProps } from '~/src/features/movix-new/Popup';
import ShieldBlock from '~/src/features/movix-new/ShieldBlock';
import { getBenefitsCards } from '~/src/features/movix-new/utils';
import VideoServices from '~/src/features/movix-new/VideoServices';
import WatchNowPayAfter from '~/src/features/movix-new/WatchNowPayAfter';
import WatchWithFamily from '~/src/features/movix-new/WatchWithFamily';
import { useProgressiveImage } from '~/src/helpers/hooks/useProgressiveImage';
import TvBorderImg from '~/static/images/movix-new/tv-border.png';
import TvBorderWebp from '~/static/images/movix-new/tv-border.webp';

import BlocksAbout from './BlocksAbout';

import * as ST from './styled';

const VideoBlock = dynamic(() => import('~/src/features/movix-new/VideoBlock'));

interface IProps {
    cardsInfo: IShopItem[],
    zasmotrisInfo: IBundle[],
}

export interface IClick {
    textButton?: string,
    leasingDuration?: number,
    salePackageId?: number,
    costType?: number,
    label: ELABELS,
}

const INDEX_MOVIX = 1;

const MovixnewLanding: React.FC<IProps> = ({ cardsInfo }) => {
    const [readyPlayVideo, setPlayVideoStatus] = useState<boolean>(false);

    const backgroundImage = useWebp() ? TvBorderWebp : TvBorderImg;
    const sourseBackgroundImage = useProgressiveImage(backgroundImage, { timeoutDelay: 1, hasTimeout: true });

    const onUnloadHandler = () => {
        setPlayVideoStatus(true);
    };

    useEffect(() => {
        window.addEventListener('load', onUnloadHandler);
        return () => {
            window.removeEventListener('load', onUnloadHandler);
        };
    }, []);

    const containerContentRef = useRef<HTMLDivElement>(null);
    const [textButton, setTextButton] = useState('');
    const [label, setLabel] = useState('');

    const [popup, setPopup] = useState<IPopupProps>({
        isOrderOpened: false,
        isResultOpened: false,
        resultTitle: '',
        resultText: '',
        resultType: EResultPopupStatus.SUCCESS,
        leasingDuration: undefined,
        salePackageId: undefined,
        costType: EEquipmentType.loan,
    });
    const [isLoading, setIsLoading] = useState(false);
    const handleClick = useCallback((data: IClick) => {
        setTextButton(data?.textButton || '');
        setLabel(data.label);
        setPopup(state => ({
            ...state,
            leasingDuration: data?.leasingDuration || popup.leasingDuration,
            salePackageId: data?.salePackageId || popup.salePackageId,
            costType: data?.costType || popup.costType,
            isOrderOpened: true,
        }));
    }, []);

    getBenefitsCards(true);
    return (
        <ST.LandingWrapper
            ref={containerContentRef}
        >
            <Header handleClick={handleClick}/>
            <ST.ServicesAndTvBlock>
                <ST.VideoContainer backgroundImg={sourseBackgroundImage}>
                    <VideoBlock readyPlayVideo={readyPlayVideo}/>
                </ST.VideoContainer>
                <VideoServices/>
            </ST.ServicesAndTvBlock>
            <MovixBenefits/>
            <WatchNowPayAfter handleClick={handleClick}/>
            <FilmsBlock/>
            <ShieldBlock/>
            <ST.Content>
                <ST.Container>
                    <ST.Title
                        marginBottom={32}
                        styles={css`
                          @media (max-width: ${breakPoints.mobile}) {
                            margin-bottom: 50px;
                          }
                        `}
                    >
                        <h2>
                            Подключить просто, использовать — ещё проще
                        </h2>
                    </ST.Title>
                    <ConnectionInfo/>
                </ST.Container>
            </ST.Content>
            <ST.Footer>
                <ST.Container>
                    <ST.Title marginBottom={44}>
                        <h2>
                            Варианты покупки приставки Movix
                        </h2>
                    </ST.Title>
                    <ST.Cards>
                        {CARDS.map((item, i) => (
                            <Card
                                key={item.id}
                                item={item}
                                handleClick={handleClick}
                                priceOptions={i === INDEX_MOVIX ? getPriceOptions(convertShopPrices(cardsInfo[0])) : undefined}
                            />
                        ))}
                    </ST.Cards>
                    <WatchWithFamily/>
                    <BlocksAbout/>
                    <ST.InfoText>{TextInfo}</ST.InfoText>
                </ST.Container>
            </ST.Footer>
            <Popup
                setPopup={setPopup}
                popup={popup}
                textButton={textButton}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                label={label}
            />
            {isLoading && <ST.StyledTrobber/>}
        </ST.LandingWrapper>
    );
};

export default MovixnewLanding;
